import React, { useEffect, useMemo, useState } from "react";
import {
  collectionGroup,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
} from "firebase/firestore";
import { Helmet } from "react-helmet-async";
import * as XLSX from "xlsx";

import Sidebar from "../../DesignSystem/Sidebar";
import { dbLOA } from "../../../backend/firebaseConfig";
import "../../../css/LOA/ProCedingChargesPage.css";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";

const FETCH_LIMIT = 500;
const CHARGE_FOUND_THRESHOLD = 14; // Schemes with more than this number of Found answers are charged
const CHARGE_PER_SCHEME = 1;
const FREE_ANALYSES_COUNT = 10;

const normalise = (value) => (value || "").toString().trim().toLowerCase();

const formatCurrency = (value) => `£${Number(value || 0).toFixed(2)}`;

const getTimestampMs = (value) => {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (value?.toDate) return value.toDate().getTime();

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getCreatedMs = (item) =>
  getTimestampMs(item.createdAtMs) ||
  getTimestampMs(item.createdAt) ||
  getTimestampMs(item.updatedAtMs) ||
  getTimestampMs(item.updatedAt) ||
  0;

const formatDate = (ms) => {
  if (!ms) return "—";

  return new Date(ms).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getMonthLabel = (ms) => {
  if (!ms) return "Unknown";

  return new Date(ms).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
};

const safeFileName = (value) =>
  normalise(value)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const getCountsFromAnswers = (answers = []) => {
  return answers.reduce(
    (acc, row) => {
      const status = normalise(row?.status);

      if (status === "found") acc.found += 1;
      else if (status === "partial") acc.partial += 1;
      else acc.missing += 1;

      return acc;
    },
    {
      found: 0,
      partial: 0,
      missing: 0,
    },
  );
};

const hasRealCounts = (counts) => {
  return (
    counts &&
    ((counts.found || 0) > 0 ||
      (counts.partial || 0) > 0 ||
      (counts.missing || 0) > 0)
  );
};

const getItemCounts = (item = {}) => {
  if (hasRealCounts(item.counts)) {
    return {
      found: item.counts.found || 0,
      partial: item.counts.partial || 0,
      missing: item.counts.missing || 0,
    };
  }

  return getCountsFromAnswers(
    item.extractionJson?.answers || item.answers || [],
  );
};

const getSchemeCounts = (scheme = {}) => {
  if (hasRealCounts(scheme.counts)) {
    return {
      found: scheme.counts.found || 0,
      partial: scheme.counts.partial || 0,
      missing: scheme.counts.missing || 0,
    };
  }

  return getCountsFromAnswers(
    scheme.extractionJson?.answers || scheme.answers || [],
  );
};

const getAnswerValue = (item, question) => {
  const answers = item?.extractionJson?.answers || item?.answers || [];

  return (
    answers.find((answer) => answer.question === question)?.answer ||
    answers.find((answer) => normalise(answer.question) === normalise(question))
      ?.answer ||
    ""
  );
};

const getClientName = (item) =>
  item?.summary?.clientName ||
  item?.extractionJson?.summary?.clientName ||
  getAnswerValue(item, "Client's Name") ||
  "Client not found";

const getProviderName = (item) =>
  item?.summary?.providerName ||
  item?.extractionJson?.summary?.providerName ||
  getAnswerValue(item, "Pension Provider Name") ||
  item?.name ||
  "Provider not found";

const getPolicyNumber = (item) =>
  item?.summary?.policyNumber ||
  item?.extractionJson?.summary?.policyNumber ||
  getAnswerValue(item, "Policy Number or Plan Number") ||
  "";

const getSchemeCharge = (foundCount) =>
  Number(foundCount || 0) > CHARGE_FOUND_THRESHOLD ? CHARGE_PER_SCHEME : 0;

const shouldIncludeExtraction = (item = {}) => {
  if (item.status === "completed") return true;

  if (Array.isArray(item.schemes) && item.schemes.length > 0) {
    return item.schemes.some(
      (scheme) =>
        scheme.extractionJson ||
        Array.isArray(scheme.answers) ||
        hasRealCounts(scheme.counts),
    );
  }

  return (
    item.extractionJson ||
    Array.isArray(item.answers) ||
    hasRealCounts(item.counts) ||
    item.summary
  );
};

const backPropagateCedingSchemeParents = async (items = []) => {
  const uniqueOwners = Array.from(
    new Set(items.map((item) => normalise(item.ownerEmail)).filter(Boolean)),
  );

  if (!uniqueOwners.length) return;

  await Promise.allSettled(
    uniqueOwners.map((ownerEmail) =>
      setDoc(
        doc(dbLOA, "Ceding Schemes", ownerEmail),
        {
          ownerEmail,
          updatedAt: serverTimestamp(),
          backPropagatedFromMonthlyStats: true,
        },
        { merge: true },
      ),
    ),
  );
};

const fetchAllExtractions = async () => {
  const allDocs = [];
  let lastDoc = null;
  let hasMore = true;

  while (hasMore) {
    const q = lastDoc
      ? query(
          collectionGroup(dbLOA, "Extractions"),
          orderBy("createdAt", "desc"),
          startAfter(lastDoc),
          limit(FETCH_LIMIT),
        )
      : query(
          collectionGroup(dbLOA, "Extractions"),
          orderBy("createdAt", "desc"),
          limit(FETCH_LIMIT),
        );

    const snapshot = await getDocs(q);

    allDocs.push(...snapshot.docs);

    lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
    hasMore = snapshot.docs.length === FETCH_LIMIT;
  }

  return allDocs.map((docSnap) => ({
    id: docSnap.id,
    ownerEmail: normalise(docSnap.ref?.parent?.parent?.id),
    ...docSnap.data(),
  }));
};

const buildSchemeRowsFromExtraction = (extraction) => {
  const createdMs = getCreatedMs(extraction);

  const ownerEmail =
    normalise(extraction.ownerEmail) ||
    normalise(extraction.adviserEmail) ||
    normalise(extraction.userEmail) ||
    normalise(extraction.createdBy) ||
    "unknown";

  const base = {
    extractionId: extraction.id,
    createdMs,
    month: getMonthLabel(createdMs),
    ownerEmail,
    createdBy: extraction.createdBy || extraction.userEmail || ownerEmail,
    status: extraction.status || "completed",
  };

  if (Array.isArray(extraction.schemes) && extraction.schemes.length > 0) {
    return extraction.schemes.map((scheme, schemeIndex) => {
      const counts = getSchemeCounts(scheme);
      const found = counts.found || 0;
      const charge = getSchemeCharge(found);

      const clientName =
        getClientName(scheme) !== "Client not found"
          ? getClientName(scheme)
          : getClientName(extraction);

      const providerName =
        getProviderName(scheme) !== "Provider not found"
          ? getProviderName(scheme)
          : getProviderName(extraction);

      return {
        ...base,
        id: `${ownerEmail}-${extraction.id}-${schemeIndex}`,
        schemeIndex,
        schemeNumber: schemeIndex + 1,
        schemeCount: extraction.schemes.length,
        clientName,
        providerName,
        policyNumber: getPolicyNumber(scheme) || getPolicyNumber(extraction),
        found,
        partial: counts.partial || 0,
        missing: counts.missing || 0,
        charge,
        chargeable: charge > 0,
      };
    });
  }

  const counts = getItemCounts(extraction);
  const found = counts.found || 0;
  const charge = getSchemeCharge(found);

  return [
    {
      ...base,
      id: `${ownerEmail}-${extraction.id}-single`,
      schemeIndex: 0,
      schemeNumber: 1,
      schemeCount: 1,
      clientName: getClientName(extraction),
      providerName: getProviderName(extraction),
      policyNumber: getPolicyNumber(extraction),
      found,
      partial: counts.partial || 0,
      missing: counts.missing || 0,
      charge,
      chargeable: charge > 0,
    },
  ];
};

const buildCaseBreakdown = (rows = []) => {
  const caseMap = new Map();

  rows.forEach((row) => {
    if (!caseMap.has(row.extractionId)) {
      caseMap.set(row.extractionId, {
        caseKey: row.extractionId,
        createdMs: row.createdMs,
        ownerEmail: row.ownerEmail,
        createdBy: row.createdBy,
        clientName: row.clientName,
        schemes: 0,
        found: 0,
        partial: 0,
        missing: 0,
        chargeableSchemes: 0,
        totalCharge: 0,
        rows: [],
      });
    }

    const item = caseMap.get(row.extractionId);

    item.schemes += 1;
    item.found += row.found || 0;
    item.partial += row.partial || 0;
    item.missing += row.missing || 0;
    item.chargeableSchemes += row.chargeable ? 1 : 0;
    item.totalCharge += row.charge || 0;
    item.rows.push(row);
  });

  return Array.from(caseMap.values()).sort((a, b) => b.createdMs - a.createdMs);
};

const createWorksheet = (headers, rows, columnWidths = []) => {
  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  worksheet["!cols"] = columnWidths.length
    ? columnWidths.map((wch) => ({ wch }))
    : headers.map((header) => ({
        wch: Math.max(14, String(header).length + 4),
      }));

  return worksheet;
};

const ProCedingChargesPage = () => {
  const [extractions, setExtractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [downloading, setDownloading] = useState(false);

  const [expandedMonths, setExpandedMonths] = useState(() => new Set());
  const [expandedCases, setExpandedCases] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;

    const loadExtractions = async () => {
      try {
        setLoading(true);
        setError("");
        setLoadingMessage("Fetching ProCeding extractions...");

        const loaded = await fetchAllExtractions();

        setLoadingMessage("Preparing monthly stats...");

        await backPropagateCedingSchemeParents(loaded);

        const completed = loaded
          .filter(shouldIncludeExtraction)
          .sort((a, b) => getCreatedMs(b) - getCreatedMs(a));

        if (!cancelled) {
          setExtractions(completed);
        }
      } catch (err) {
        console.error("Failed to load ProCeding monthly stats:", err);

        if (!cancelled) {
          setError(
            err.message ||
              "Failed to load monthly stats. Check the Firestore index for createdAt ordering.",
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingMessage("");
        }
      }
    };

    loadExtractions();

    return () => {
      cancelled = true;
    };
  }, []);

  const schemeRows = useMemo(() => {
    const rows = extractions.flatMap(buildSchemeRowsFromExtraction);

    // Filter out staff with @rosemountfs.co.uk email
    const filteredByDomain = rows.filter(
      (row) => !row.ownerEmail.endsWith("@rosemountfs.co.uk"),
    );

    // The first FREE_ANALYSES_COUNT analyses PER ADVISER (by date) are free,
    // regardless of whether they would otherwise meet the charging threshold.
    const sorted = [...filteredByDomain].sort(
      (a, b) => a.createdMs - b.createdMs,
    );
    const adviserFreeCount = new Map(); // Track free count per adviser
    const freeIds = new Set();

    for (const row of sorted) {
      const adviser = normalise(row.ownerEmail);
      const currentFreeCount = adviserFreeCount.get(adviser) || 0;

      if (currentFreeCount < FREE_ANALYSES_COUNT) {
        freeIds.add(row.id);
        adviserFreeCount.set(adviser, currentFreeCount + 1);
      }
    }

    return filteredByDomain.map((row) =>
      freeIds.has(row.id)
        ? { ...row, charge: 0, chargeable: false, free: true }
        : row,
    );
  }, [extractions]);

  const filteredRows = useMemo(() => {
    const term = normalise(searchTerm);

    if (!term) return schemeRows;

    return schemeRows.filter((row) =>
      [
        row.clientName,
        row.providerName,
        row.policyNumber,
        row.ownerEmail,
        row.createdBy,
        row.status,
      ]
        .map(normalise)
        .some((value) => value.includes(term)),
    );
  }, [schemeRows, searchTerm]);

  const monthlyStats = useMemo(() => {
    const map = new Map();

    filteredRows.forEach((row) => {
      if (!map.has(row.month)) {
        map.set(row.month, {
          month: row.month,
          createdMs: row.createdMs,
          casesSet: new Set(),
          schemes: 0,
          found: 0,
          partial: 0,
          missing: 0,
          chargeableSchemes: 0,
          totalCharge: 0,
          rows: [],
        });
      }

      const month = map.get(row.month);

      month.casesSet.add(row.extractionId);
      month.schemes += 1;
      month.found += row.found || 0;
      month.partial += row.partial || 0;
      month.missing += row.missing || 0;
      month.chargeableSchemes += row.chargeable ? 1 : 0;
      month.totalCharge += row.charge || 0;
      month.rows.push(row);
    });

    return Array.from(map.values())
      .map((month) => ({
        ...month,
        cases: month.casesSet.size,
        totalAnswers: month.found + month.partial + month.missing,
        caseBreakdown: buildCaseBreakdown(month.rows),
      }))
      .sort((a, b) => b.createdMs - a.createdMs);
  }, [filteredRows]);

  const toggleMonth = (monthLabel) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      next.has(monthLabel) ? next.delete(monthLabel) : next.add(monthLabel);
      return next;
    });
  };

  const toggleCase = (caseKey) => {
    setExpandedCases((prev) => {
      const next = new Set(prev);
      next.has(caseKey) ? next.delete(caseKey) : next.add(caseKey);
      return next;
    });
  };

  const downloadMonth = (month) => {
    if (!month?.rows?.length) return;

    setDownloading(true);

    try {
      const workbook = XLSX.utils.book_new();

      const completion =
        month.totalAnswers > 0
          ? Math.round((month.found / month.totalAnswers) * 100)
          : 0;

      const summarySheet = createWorksheet(
        [
          "Month",
          "Cases",
          "Schemes",
          "Chargeable Schemes",
          "Free Schemes",
          "Found",
          "Partial",
          "Missing",
          "Completion %",
          "Charge Rule",
          "Total Charge",
        ],
        [
          [
            month.month,
            month.cases,
            month.schemes,
            month.chargeableSchemes,
            month.schemes - month.chargeableSchemes,
            month.found,
            month.partial,
            month.missing,
            `${completion}%`,
            `£${CHARGE_PER_SCHEME} per scheme where Found > ${CHARGE_FOUND_THRESHOLD}`,
            formatCurrency(month.totalCharge),
          ],
        ],
        [20, 12, 12, 20, 16, 12, 12, 12, 16, 42, 16],
      );

      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

      const caseSheet = createWorksheet(
        [
          "Date",
          "Client Name",
          "Owner / Adviser Email",
          "Submitted By",
          "Schemes",
          "Chargeable Schemes",
          "Free Schemes",
          "Found",
          "Partial",
          "Missing",
          "Case Charge",
        ],
        month.caseBreakdown.map((caseItem) => [
          formatDate(caseItem.createdMs),
          caseItem.clientName,
          caseItem.ownerEmail,
          caseItem.createdBy,
          caseItem.schemes,
          caseItem.chargeableSchemes,
          caseItem.schemes - caseItem.chargeableSchemes,
          caseItem.found,
          caseItem.partial,
          caseItem.missing,
          formatCurrency(caseItem.totalCharge),
        ]),
        [16, 28, 36, 36, 12, 20, 14, 12, 12, 12, 14],
      );

      XLSX.utils.book_append_sheet(workbook, caseSheet, "Case Breakdown");

      const schemeSheet = createWorksheet(
        [
          "Date",
          "Client Name",
          "Scheme No.",
          "Provider Name",
          "Policy Number",
          "Owner / Adviser Email",
          "Submitted By",
          "Found",
          "Partial",
          "Missing",
          "Chargeable",
          "First 10",
          "Charge",
          "Status",
        ],
        month.rows.map((row) => [
          formatDate(row.createdMs),
          row.clientName,
          row.schemeNumber,
          row.providerName,
          row.policyNumber || "-",
          row.ownerEmail,
          row.createdBy,
          row.found,
          row.partial,
          row.missing,
          row.chargeable ? "Yes" : "No",
          row.free ? "Yes" : "No",
          formatCurrency(row.charge),
          row.status,
        ]),
        [16, 28, 12, 30, 24, 36, 36, 12, 12, 12, 14, 12, 12, 16],
      );

      XLSX.utils.book_append_sheet(workbook, schemeSheet, "Scheme Charges");

      XLSX.writeFile(
        workbook,
        `proceding-charges-${safeFileName(month.month)}.xlsx`,
      );
    } catch (err) {
      console.error("Failed to download monthly stats:", err);
      setError(err.message || "Failed to download monthly stats.");
    } finally {
      setDownloading(false);
    }
  };

  const totalCases = monthlyStats.reduce((sum, month) => sum + month.cases, 0);

  const totalSchemes = monthlyStats.reduce(
    (sum, month) => sum + month.schemes,
    0,
  );

  const totalCharge = monthlyStats.reduce(
    (sum, month) => sum + month.totalCharge,
    0,
  );

  const renderSchemeList = (schemes = []) => {
    return (
      <div className="loa-scheme-inline-list">
        {schemes.map((scheme) => (
          <div key={scheme.id} className="loa-scheme-inline-item">
            <div className="loa-scheme-inline-top">
              <strong>Scheme {scheme.schemeNumber}</strong>

              <span
                className={
                  scheme.chargeable ? "loa-charge-yes" : "loa-charge-no"
                }
              >
                {scheme.chargeable ? formatCurrency(scheme.charge) : "£0.00"}
              </span>
            </div>

            <span>{scheme.providerName}</span>

            {scheme.policyNumber && <small>{scheme.policyNumber}</small>}

            <div className="loa-status-stack loa-status-stack-small">
              <span className="loa-answer-pill found">
                Found: {scheme.found}
              </span>
              <span className="loa-answer-pill partial">
                Partial: {scheme.partial}
              </span>
              <span className="loa-answer-pill missing">
                Missing: {scheme.missing}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };
  const renderStatusBreakdown = ({ found = 0, partial = 0, missing = 0 }) => {
    return (
      <div className="loa-status-stack">
        <span className="loa-answer-pill found">Found: {found}</span>
        <span className="loa-answer-pill partial">Partial: {partial}</span>
        <span className="loa-answer-pill missing">Missing: {missing}</span>
      </div>
    );
  };

  return (
    <div className="main-content">
      <Helmet>
        <title>ProCeding Monthly Stats</title>
      </Helmet>

      <Sidebar />

      <div className="Main-Container">
        <div className="loa-dashboard">
          <Breadcrumb
            items={[
              {
                label: "Finance",
                to: "/Department/Finance",
              },
              { label: "ProCeding Statements" },
            ]}
          />
          <section className="loa-hero-card">
            <div>
              <span className="loa-eyebrow-pill">ProCeding</span>
              <h1>Monthly Stats</h1>
              <p>
                View ProCeding cases by month, with case and individual scheme
                breakdowns.
              </p>
            </div>
          </section>

          <section className="loa-client-card">
            {/* <div className="loa-dashboard-toolbar">
              <div className="loa-dashboard-search">
                <input
                  type="text"
                  value={searchTerm}
                  placeholder="Search client, provider, policy or adviser email..."
                  onChange={(event) => setSearchTerm(event.target.value)}
                />

                {searchTerm && (
                  <button type="button" onClick={() => setSearchTerm("")}>
                    Clear
                  </button>
                )}
              </div>
            </div> */}

            <p className="loa-dashboard-result-count FP_Content black">
              {totalCases} case{totalCases !== 1 ? "s" : ""} · {totalSchemes}{" "}
              scheme{totalSchemes !== 1 ? "s" : ""} · {monthlyStats.length}{" "}
              month{monthlyStats.length !== 1 ? "s" : ""} ·{" "}
              {formatCurrency(totalCharge)} total
            </p>
          </section>

          {error && <div className="loa-error-banner">{error}</div>}

          {loading ? (
            <section className="loa-results-card loa-elevated-scheme-card">
              <section className="loa-processing-card">
                <div className="loa-spinner" />
                <div>
                  <h2>Loading monthly stats...</h2>
                  <p>{loadingMessage || "Fetching ProCeding extractions."}</p>
                </div>
              </section>
            </section>
          ) : monthlyStats.length === 0 ? (
            <div className="loa-empty-card">
              No ProCeding extractions found.
            </div>
          ) : (
            monthlyStats.map((month) => {
              const completion =
                month.totalAnswers > 0
                  ? Math.round((month.found / month.totalAnswers) * 100)
                  : 0;

              const isMonthExpanded = expandedMonths.has(month.month);

              return (
                <section
                  className="loa-results-card loa-elevated-scheme-card loa-month-group-card"
                  key={month.month}
                >
                  <div className="loa-card-header">
                    <div>
                      <span className="loa-section-label">Monthly Table</span>
                      <h2>{month.month}</h2>
                      <p>
                        £{CHARGE_PER_SCHEME} is charged per scheme where more
                        than {CHARGE_FOUND_THRESHOLD} points are
                        successfully identified.
                      </p>
                    </div>

                    <div className="loa-upload-actions">
                      <button
                        type="button"
                        className="loa-secondary-btn"
                        onClick={() => toggleMonth(month.month)}
                      >
                        {isMonthExpanded ? "Hide Breakdown" : "View Breakdown"}
                      </button>

                      <button
                        type="button"
                        className="loa-primary-btn"
                        onClick={() => downloadMonth(month)}
                        disabled={downloading}
                      >
                        Download Excel
                      </button>
                    </div>
                  </div>

                  <div className="loa-table-wrap">
                    <table className="loa-table">
                      <thead>
                        <tr>
                          <th>Cases</th>
                          <th>Schemes</th>
                          <th>Chargeable</th>
                          <th>Free</th>
                          <th>Found</th>
                          <th>Partial</th>
                          <th>Missing</th>
                          <th>Completion</th>
                          <th>Total Charge</th>
                        </tr>
                      </thead>

                      <tbody>
                        <tr>
                          <td>{month.cases}</td>
                          <td>{month.schemes}</td>
                          <td>{month.chargeableSchemes}</td>
                          <td>{month.schemes - month.chargeableSchemes}</td>
                          <td>
                            <span className="loa-answer-pill found">
                              {month.found}
                            </span>
                          </td>
                          <td>
                            <span className="loa-answer-pill partial">
                              {month.partial}
                            </span>
                          </td>
                          <td>
                            <span className="loa-answer-pill missing">
                              {month.missing}
                            </span>
                          </td>
                          <td>{completion}%</td>
                          <td>
                            <strong>{formatCurrency(month.totalCharge)}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {isMonthExpanded && (
                    <div className="loa-ceding-breakdown">
                      <div className="loa-ceding-breakdown-header">
                        <strong>{month.month} Case Breakdown</strong>
                        <span>
                          {month.caseBreakdown.length} case
                          {month.caseBreakdown.length !== 1 ? "s" : ""}
                        </span>
                      </div>

                      <div className="loa-table-wrap">
                        <table className="loa-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Client</th>
                              <th>Owner / Adviser</th>
                              <th>Schemes</th>
                              {/* <th>Scheme Breakdown</th> */}
                            </tr>
                          </thead>
                          <tbody>
                            {month.caseBreakdown.map((caseItem) => {
                              const isCaseExpanded = expandedCases.has(
                                caseItem.caseKey,
                              );

                              return (
                                <React.Fragment key={caseItem.caseKey}>
                                  <tr>
                                    <td>{formatDate(caseItem.createdMs)}</td>

                                    <td className="loa-question-cell">
                                      {caseItem.clientName}
                                    </td>

                                    <td>{caseItem.ownerEmail}</td>

                                    <td>{renderSchemeList(caseItem.rows)}</td>

                                    {/* <td>
                                      <button
                                        type="button"
                                        className="loa-secondary-btn"
                                        onClick={() =>
                                          toggleCase(caseItem.caseKey)
                                        }
                                      >
                                        {isCaseExpanded ? "Hide" : "View"}
                                      </button>
                                    </td> */}
                                  </tr>
                                  {/* {isCaseExpanded && (
                                    <tr className="loa-breakdown-row">
                                      <td colSpan={4}>
                                        <div className="loa-ceding-breakdown">
                                          <div className="loa-ceding-breakdown-header">
                                            <strong>
                                              Individual Scheme Breakdown
                                            </strong>
                                            <span>
                                              {caseItem.rows.length} scheme
                                              {caseItem.rows.length !== 1
                                                ? "s"
                                                : ""}
                                            </span>
                                          </div>

                                          <div className="loa-table-wrap">
                                            <table className="loa-table">
                                              <thead>
                                                <tr>
                                                  <th>Scheme No.</th>
                                                  <th>Provider</th>
                                                  <th>Policy Number</th>
                                                  <th>Found</th>
                                                  <th>Partial</th>
                                                  <th>Missing</th>
                                                  <th>Chargeable</th>
                                                  <th>Charge</th>
                                                </tr>
                                              </thead>

                                              <tbody>
                                                {caseItem.rows.map((scheme) => (
                                                  <tr key={scheme.id}>
                                                    <td>
                                                      {scheme.schemeNumber}
                                                    </td>
                                                    <td className="loa-question-cell">
                                                      {scheme.providerName}
                                                    </td>
                                                    <td>
                                                      {scheme.policyNumber ||
                                                        "—"}
                                                    </td>
                                                    <td>{scheme.found}</td>
                                                    <td>{scheme.partial}</td>
                                                    <td>{scheme.missing}</td>
                                                    <td>
                                                      {scheme.chargeable
                                                        ? "Yes"
                                                        : "No"}
                                                    </td>
                                                    <td>
                                                      <strong>
                                                        {formatCurrency(
                                                          scheme.charge,
                                                        )}
                                                      </strong>
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )} */}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </section>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ProCedingChargesPage;
